/**
 * The feature flags the settings endpoint hands out, and the questions the pages ask of them.
 *
 * A flag is a settings row with `featureFlag` set: `name` says which flag it is, `value` says
 * whether it is on, and `ownableId` names the user it was granted to. Admins are handed every
 * settings row and everyone else only the flags that reach them, so one lookup serves both
 * listings.
 *
 * The 2FA bypass exempts a single account from the second factor — sign-in hands it a full session
 * on the password alone, so it is never asked to enrol either. The database caps the flag at one
 * row, which is why `tfaBypassFlag` looks for a single one rather than filtering per user: a flag
 * already granted elsewhere is the reason an admin cannot grant another, and finding whose it is
 * takes reading the row that exists.
 */
import {findRecordByAttribute} from './data'
import {isEmpty} from './tools'

export const TFA_BYPASS = 'TFA_BYPASS'
export const ENABLED = 'enabled'
export const DISABLED = 'disabled'

export const tfaBypassFlag = (settings = []) => findRecordByAttribute({records: settings, attribute: 'name', id: TFA_BYPASS})

export const isTfaBypassed = ({settings, userId}) => {
  const flag = tfaBypassFlag(settings)

  return Boolean(userId) && flag.ownableId === userId && flag.value === ENABLED
}

/**
 * Whether the account part way through a sign-in is the one the bypass was granted to, on or off.
 *
 * The value is no part of this, unlike `isTfaBypassed`: an account holding the flag does not use a
 * second factor at all, and a sign-in that reaches for this owes it an explanation rather than an
 * enrolment. Owner is what cannot be read here — the id `isTfaBypassed` compares does not exist
 * until the sign-in finishes, leaving the flag's presence as all there is to go on. That reads
 * correctly for a plain user, who is handed only the flags that reach them, and wrongly for an
 * admin, who is handed every account's: someone else's bypass would otherwise strand an admin at
 * the disabled notice instead of asking for their second factor.
 *
 * Roles do arrive before the session does, so the flag is taken as this account's only for one
 * confirmed to be a plain user. Roles that could not be read leave it disowned, which puts every
 * uncertain sign-in through the second factor rather than past it.
 */
export const holdsTfaBypass = ({settings, roles = []}) => {
  const role = roles[0] || {}

  return role.name === 'user' && !isEmpty(tfaBypassFlag(settings))
}

/**
 * The two lists the admin settings page shows, taken from the one listing an admin is handed.
 *
 * TFA_BYPASS is split out by name rather than by whether a row carries an owner, because it is the
 * only flag the code knows in advance: the sign-in recognises it by name, and the account it belongs
 * to is chosen from that account's own page. Every other flag is named by whoever created it and
 * carries an owner only when they scoped it to one, which leaves the rows themselves as the whole
 * list either way.
 */
export const managedFlags = (settings = []) => settings.filter(setting => setting.featureFlag && setting.name !== TFA_BYPASS)

export const plainSettings = (settings = []) => settings.filter(setting => !setting.featureFlag)

export const isFlagEnabled = (flag = {}) => flag.value === ENABLED
